// Work center codes for display: "WC-001 · Assembly Line" in routing, schedule, work orders and
// reports. Work orders and routing stages carry a copy of the work center name, so a code is
// found by id first and by name second. The list is fetched once and shared by every screen.
import { useEffect, useMemo, useState } from 'react';
import { getAllWorkCenters, type WorkCenterResponse } from '@/api/workcenter.api';

let cache: WorkCenterResponse[] | null = null;
let pending: Promise<WorkCenterResponse[]> | null = null;
const listeners = new Set<(list: WorkCenterResponse[]) => void>();

function load(): Promise<WorkCenterResponse[]> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = getAllWorkCenters()
      .then((res) => {
        cache = res.data ?? [];
        listeners.forEach((l) => l(cache!));
        return cache;
      })
      .catch(() => {
        pending = null;
        return [];
      });
  }
  return pending;
}

/** Drop the cached list after a work center is added or changed; open screens reload it. */
export function invalidateWorkCenters() {
  cache = null;
  pending = null;
  if (listeners.size > 0) void load();
}

/** "WC-001 · Assembly Line", or just the name when there is no code. */
export const formatWorkCenter = (code?: string | null, name?: string | null) =>
  [code, name].filter(Boolean).join(' · ');

export interface WorkCenterLookup {
  /** Code of a work center by id, falling back to its (copied) name */
  codeOf: (id?: string | null, name?: string | null) => string;
  /** "WC-001 · Assembly Line" */
  labelOf: (id?: string | null, name?: string | null) => string;
}

export function useWorkCenters(): WorkCenterLookup {
  const [list, setList] = useState<WorkCenterResponse[]>(cache ?? []);
  useEffect(() => {
    let alive = true;
    const onLoad = (l: WorkCenterResponse[]) => alive && setList(l);
    listeners.add(onLoad);
    load().then(onLoad);
    return () => {
      alive = false;
      listeners.delete(onLoad);
    };
  }, []);

  return useMemo(() => {
    const byId = new Map(list.map((wc) => [String(wc.id), wc]));
    const byName = new Map(list.map((wc) => [wc.name.trim().toLowerCase(), wc]));
    const find = (id?: string | null, name?: string | null) =>
      (id ? byId.get(String(id)) : undefined) ?? (name ? byName.get(name.trim().toLowerCase()) : undefined);
    const codeOf = (id?: string | null, name?: string | null) => find(id, name)?.code ?? '';
    const labelOf = (id?: string | null, name?: string | null) => {
      const wc = find(id, name);
      return formatWorkCenter(wc?.code, name || wc?.name);
    };
    return { codeOf, labelOf };
  }, [list]);
}
